import DocumentIcon from "@/components/icons/DocumentIcon";
import VisibilityIcon from "@/components/icons/VisibilityIcon";
import Modal, { ModalProvider, ModalTrigger } from "@/components/Modal/Modal";
import type { EvidenceAttachmentModel } from "@/api/cle-api";

import { formatDateTime } from "./utils/formatters";
import { formatAttachmentSize } from "./utils/attachmentSize";
import { SupplementaryDocumentDownloadButton } from "./SupplementaryDocumentDownloadButton";
import { SupplementaryDocumentPreview } from "./SupplementaryDocumentPreview";
import type { SupplementaryDocumentKind } from "./types";

interface SupplementaryDocumentsProps {
  evidence: { id: string; attachments?: EvidenceAttachmentModel[] | null };
  kind: SupplementaryDocumentKind;
  userId: string;
}

/** Linjen under overskriften. Kursusbeviser analyseres af AI'en, så det er
 * værd at sige, at bilag ikke gør; undervisning analyseres slet ikke. */
const DESCRIPTIONS: Record<SupplementaryDocumentKind, string> = {
  kursusbevis:
    "Vedhæftet af medlemmet. Bilag er ikke kursusbeviser: de er ikke analyseret og giver ingen lektioner i sig selv.",
  undervisning: "Vedhæftet af medlemmet som supplement til undervisningen.",
};

/**
 * Bilag, medlemmet har vedhæftet et kursusbevis eller en undervisning (ADV-359)
 * for at underbygge det.
 *
 * Kortet skjules helt, når der ingen bilag er: en tom rubrik på hvert eneste
 * bevis ville bare skubbe det, sagsbehandleren faktisk skal læse, længere ned
 * på siden.
 */
export const SupplementaryDocuments = ({
  evidence,
  kind,
  userId,
}: SupplementaryDocumentsProps) => {
  const attachments = evidence.attachments ?? [];

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="c-card c-item-list">
      <h3 className="flex-100">Supplerende dokumentation</h3>
      <p className="flex-100 text-muted small mb-2">{DESCRIPTIONS[kind]}</p>
      <ul
        className="flex-100 list-unstyled d-flex flex-column gap-2 mb-0"
        style={{ minWidth: 0 }}
      >
        {attachments.map((attachment) => (
          <li
            key={attachment.id}
            className="d-flex align-items-center gap-3 border rounded p-2"
          >
            <DocumentIcon height={18} />
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="text-truncate fw-semibold">
                {attachment.fileName}
              </div>
              <div className="text-muted small">
                {formatAttachmentSize(attachment.sizeBytes)} ·{" "}
                {formatDateTime(attachment.createdAt)}
                {attachment.createdByUserName
                  ? ` · ${attachment.createdByUserName}`
                  : ""}
              </div>
            </div>
            <ModalProvider>
              <ModalTrigger
                asChild
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
              >
                <button type="button">
                  <VisibilityIcon height={16} />
                  Vis
                </button>
              </ModalTrigger>
              <Modal
                title={
                  // max-width er det, der goer arbejdet. min-width: 0 paa
                  // spannet var ikke nok: det er ikke spannet, der naegter at
                  // krympe, men .c-modal-title omkring det - en flex-boks med
                  // min-width: auto, hvis indholdsbaserede minimum er hele den
                  // ubrudte streng. Et barns min-content-bidrag klippes af dets
                  // egen max-width, saa den her holder baade titlen og dialogen
                  // inde. Smallere end dokumentvisningen, saa det er den, der
                  // bestemmer dialogens bredde.
                  <span
                    className="text-truncate"
                    style={{
                      minWidth: 0,
                      maxWidth: "calc(min(80vw, 900px) - 8rem)",
                    }}
                    title={attachment.fileName}
                  >
                    {attachment.fileName}
                  </span>
                }
                footer={
                  <SupplementaryDocumentDownloadButton
                    attachmentId={attachment.id}
                    evidenceId={evidence.id}
                    kind={kind}
                    userId={userId}
                  />
                }
              >
                <SupplementaryDocumentPreview
                  attachmentId={attachment.id}
                  evidenceId={evidence.id}
                  fileName={attachment.fileName}
                  kind={kind}
                  userId={userId}
                />
              </Modal>
            </ModalProvider>
          </li>
        ))}
      </ul>
    </div>
  );
};
